﻿import { getCurrentUser } from "@/utils/auth";
import { api, request, SRM_BASE, EMISSION_BASE } from "@/api/client";
import { downloadBlob } from "@/lib/download";
import { uploadFile } from "@/api/upload";
import { MONTHS } from "@/lib/date";
import {
  registerImportFile as registerImportFileShared,
  getImportFiles as getImportFilesShared,
  getImportFileStatus as getImportFileStatusShared,
  removeImportFile as removeImportFileShared,
  getSampleFile as getSampleFileShared,
} from "@/features/emissions/api/importFileService";
import type {
  SupplierDetail,
  SupplierCreatePayload,
  SupplierListItem,
  SupplierListResponse,
  SupplierStatusAggregate,
} from "../types/suppliers";
import type {
  ImportFile,
  ImportFileListResponse,
} from "@/features/emissions/api/importFileService";

export type {
  ImportFile,
  ImportFileStatus,
  ImportFileListResponse,
} from "@/features/emissions/api/importFileService";

interface RawSupplier {
  id: number;
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: {
    addressLine: string;
    city: string;
    state: string;
    country: string;
  };
  disabled?: boolean;
  activationDate?: string;
  logo?: string;
  industry?: number;
  company?: number | null;
}

export async function getSupplierList(
  page: number,
  limit: number,
  search?: string,
): Promise<SupplierListResponse> {
  const params: Record<string, string | number> = { page, limit };
  if (search) params.search = search;
  const [rawSuppliers, countResult] = await Promise.all([
    api<RawSupplier[]>("get", `${SRM_BASE}/suppliers`, undefined, params),
    getSupplierCount(search),
  ]);
  const data: SupplierListItem[] = (
    Array.isArray(rawSuppliers) ? rawSuppliers : []
  ).map((s: RawSupplier) => ({
    id: s.id,
    name: s.name,
    contact_name: s.contactName ?? "",
    email: s.email ?? "",
    phone: s.phone ?? "",
    address: s.address ?? { addressLine: "", city: "", state: "", country: "" },
    status: s.disabled ? "Inactive" : "Active",
    registration: s.activationDate ? "Registered" : "Unregistered",
    logo: s.logo ?? "",
    industry: s.industry ?? 0,
    company: s.company ?? null,
  }));
  return { data, total: Number(countResult.count) };
}

export async function getSupplierDetail(id: number): Promise<SupplierDetail> {
  const raw = await api<Record<string, unknown>>(
    "get",
    `${SRM_BASE}/supplier/${id}`,
  );
  const prefs = (raw.preferences ?? {}) as Record<string, unknown>;
  return {
    ...raw,
    preferences: {
      frequency:
        (prefs.frequency as "Monthly" | "Quarterly" | "Yearly") ?? "Monthly",
      reportWindow:
        prefs.report_window != null ? Number(prefs.report_window) : 0,
      reportingEnabled: Boolean(prefs.reporting_enabled),
      reportingPaused: Boolean(prefs.reporting_paused),
      reportingTimezone: String(prefs.reporting_timezone || "UTC"),
      recipientTimezone: String(prefs.recipient_timezone || "UTC"),
      firstPeriod: prefs.first_period ? String(prefs.first_period) : "",
      fiscalStartMonth: Number(prefs.fiscal_start_month || 1),
      emissionsCaptured:
        (prefs.emissions_captured as "scope-1-2" | "scope-1-2-3" | "product") ??
        "scope-1-2",
    },
  } as SupplierDetail;
}

export async function createSupplier(
  payload: SupplierCreatePayload,
): Promise<{ id: number }> {
  return api("post", `${SRM_BASE}/supplier`, payload);
}

export async function updateSupplier(
  payload: SupplierCreatePayload & { id: number },
): Promise<{ success: boolean }> {
  const { id, preferences, fields, ...rest } = payload;
  const body: Record<string, unknown> = {
    ...rest,
    id,
    contact_name: rest.contactName,
    secondary_contact_name: rest.secondaryContactName,
    secondary_email: rest.secondaryEmail,
    registration_id: rest.registrationId,
    is_supplier: rest.isSupplier,
    is_borrower: rest.isBorrower,
    preferences: {
      frequency: preferences.frequency,
      report_window: preferences.reportWindow,
      reporting_enabled: preferences.reportingEnabled ?? false,
      reporting_paused: preferences.reportingPaused ?? false,
      reporting_timezone: preferences.reportingTimezone || "UTC",
      recipient_timezone: preferences.recipientTimezone || "UTC",
      first_period: preferences.firstPeriod || null,
      emissions_captured: preferences.emissionsCaptured,
    },
    custom_fields: fields,
  };
  delete body.contactName;
  delete body.secondaryContactName;
  delete body.secondaryEmail;
  delete body.registrationId;
  delete body.isSupplier;
  delete body.isBorrower;
  return api("put", `${SRM_BASE}/supplier`, body);
}

export async function updateMultipleSuppliers(
  supplierIds: number[],
  disabled: boolean,
): Promise<{ success: boolean }> {
  return api("put", `${SRM_BASE}/suppliers`, {
    ids: supplierIds,
    active: !disabled,
  });
}

export async function getSupplierStatusAggregate(): Promise<SupplierStatusAggregate> {
  return api("get", `${SRM_BASE}/supplier-status-aggregate`);
}

export async function getSupplierCount(
  search?: string,
): Promise<{ count: number; active_count?: number }> {
  const params: Record<string, string> = {};
  if (search) params.search = search;
  return api("get", `${SRM_BASE}/supplier-count`, undefined, params);
}

export async function sendRegistrationRequest(
  supplierId: number,
): Promise<{ success: boolean }> {
  return api("post", `${SRM_BASE}/supplier-link`, {
    supplier: supplierId,
  });
}

export async function getAllSuppliers(): Promise<
  Array<{
    id: number;
    name: string;
    company: number | null;
    activationDate: string | null;
  }>
> {
  const PAGE_SIZE = 500;
  const MAX_PAGES = 20;
  const results: RawSupplier[] = [];
  for (let page = 0; page < MAX_PAGES; page++) {
    const batch = await api<RawSupplier[]>(
      "get",
      `${SRM_BASE}/suppliers`,
      undefined,
      { page, limit: PAGE_SIZE },
    );
    const items = Array.isArray(batch) ? batch : [];
    results.push(...items);
    if (items.length < PAGE_SIZE) break;
  }
  return results.map((s: RawSupplier) => ({
    id: s.id,
    name: s.name,
    company: s.company ?? null,
    activationDate: s.activationDate ?? null,
  }));
}

export interface EmissionRecord {
  id: number;
  name: string;
  registration_id: string;
  company: number;
  customer: number;
  total_emissions: number;
  total_scope1: number;
  total_scope2: number;
  total_scope3: number;
  allocated_total_emissions: number;
  allocated_scope1: number;
  allocated_scope2: number;
  allocated_scope3: number;
  has_suppliers: boolean;
  children: EmissionRecord[];
}

export async function getSuppliersWithTotalEmissionRecords(params: {
  fromDate: Date;
  toDate: Date;
  searchText?: string;
  sortBy?: string;
  sortOrder?: string;
  parentCompany?: number;
}): Promise<EmissionRecord[]> {
  const raw = await api<EmissionRecord[]>(
    "get",
    `${SRM_BASE}/suppliers-total-emission-records`,
    undefined,
    {
      fromMonth: MONTHS[params.fromDate.getMonth()],
      fromYear: params.fromDate.getFullYear(),
      toMonth: MONTHS[params.toDate.getMonth()],
      toYear: params.toDate.getFullYear(),
      ...(params.searchText ? { searchText: params.searchText } : {}),
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
      ...(params.parentCompany ? { parentCompany: params.parentCompany } : {}),
    },
  );
  return (Array.isArray(raw) ? raw : []).map((r: EmissionRecord) => ({
    ...r,
    children: r.children || [],
  }));
}

export interface SupplierEmissionSourcesParams {
  fromDate: Date;
  toDate: Date;
  searchText?: string;
  sortBy?: string;
  sortOrder?: string;
  parentCompany?: number;
  allocatedOnly?: boolean;
  page: number;
  limit: number;
}

export interface SupplierEmissionSourcesPage {
  data: EmissionRecord[];
  total: number;
  totalAllocatedEmissions: number;
  page: number;
  limit: number;
}

export async function getSupplierEmissionSourcesPage(
  params: SupplierEmissionSourcesParams,
): Promise<SupplierEmissionSourcesPage> {
  const response = await request<SupplierEmissionSourcesPage>(
    "get",
    `${SRM_BASE}/suppliers-total-emission-records`,
    {
      unwrap: false,
      params: {
        paged: true,
        ...(params.allocatedOnly !== undefined
          ? { allocatedOnly: params.allocatedOnly }
          : {}),
        page: params.page,
        limit: params.limit,
        fromMonth: MONTHS[params.fromDate.getMonth()],
        fromYear: params.fromDate.getFullYear(),
        toMonth: MONTHS[params.toDate.getMonth()],
        toYear: params.toDate.getFullYear(),
        ...(params.searchText ? { searchText: params.searchText } : {}),
        ...(params.sortBy ? { sortBy: params.sortBy } : {}),
        ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
        ...(params.parentCompany
          ? { parentCompany: params.parentCompany }
          : {}),
      },
    },
  );
  if (!response || !Array.isArray(response.data)) {
    throw new Error(
      "Unable to read supplier emission sources. Please try again.",
    );
  }
  return response;
}

export interface AllocationRecord {
  approach_method: string;
  allocation_approach: string;
  allocation_method: string;
  category_name: string;
  total_production: number;
  supplied_count: number;
  sales: number;
  total_revenue: number;
  numerator: number;
  denominator: number;
  allocated_percentage: number;
  allocated_scope1: number;
  allocated_scope2: number;
  allocated_scope3: number;
  allocated_total_emissions: number;
  from_date: string;
  to_date: string;
  name: string;
  transactions: Array<{
    id: number;
    field_name: string;
    total_units: number;
    emission_factor: number;
    value: string;
  }>;
}

export async function checkDuplicateSupplier(
  field: string,
  value: string,
): Promise<{ exists: boolean; email?: string; registration_id?: string }> {
  const user = getCurrentUser();
  return api("get", `${SRM_BASE}/check-duplicate-supplier`, undefined, {
    customer: user?.companyId || "",
    field,
    value,
  });
}

export async function getSupplierAllocations(params: {
  fromDate: Date;
  toDate: Date;
  customerID: number;
  supplierID: number;
}): Promise<AllocationRecord[]> {
  const raw = await api<{ allocations: unknown[] }>(
    "get",
    `${SRM_BASE}/supplier-allocations`,
    undefined,
    {
      fromMonth: MONTHS[params.fromDate.getMonth()],
      fromYear: params.fromDate.getFullYear(),
      toMonth: MONTHS[params.toDate.getMonth()],
      toYear: params.toDate.getFullYear(),
      customerID: params.customerID,
      supplierID: params.supplierID,
    },
  );
  const allocations = (raw?.allocations || []) as AllocationRecord[];
  return allocations.map((a) => ({
    ...a,
    name: getAllocationName(
      a.approach_method,
      a.allocation_approach,
      a.allocation_method,
    ),
  }));
}

function getAllocationName(
  approachMethod: string,
  allocationApproach: string,
  allocationMethod: string,
): string {
  const method = approachMethod || "investor";
  const approach = allocationApproach || allocationMethod;
  switch (method) {
    case "allocation":
      if (approach === "production volume") return "Production volume approach";
      if (approach === "fixed percentage")
        return "Allocate by fixed percentage";
      return "Economic";
    case "product":
      return "Product carbon footprint";
    case "investor":
      if (approach === "outstanding value based")
        return "Outstanding value based allocation";
      if (approach === "fixed attribution factor")
        return "Fixed attribution factor";
      return "-";
    case "none":
      return "None";
    default:
      return "-";
  }
}

// --- Bulk upload ---

export async function uploadBulkFile(
  file: File,
): Promise<{ key: string; Location: string }> {
  return uploadFile(file, "import");
}

export async function registerImportFile(payload: {
  originalName: string;
  fileName: string;
  filePath: string;
  company: string;
  typeName: string;
  ownerName: string;
}): Promise<{ id: number }> {
  return registerImportFileShared(payload);
}

export async function getImportFiles(
  page: number,
  limit: number,
): Promise<ImportFileListResponse> {
  const user = getCurrentUser();
  return getImportFilesShared(page, limit, "Suppliers", {
    company: user?.companyId || "",
  });
}

export async function getImportFileStatus(id: number): Promise<ImportFile> {
  return getImportFileStatusShared(id);
}

export async function processSupplierBulkUpload(payload: {
  importFileId: number;
  displayName: string;
  emissionStatus: string;
  typeName: string;
  isAiAutomap: boolean;
}): Promise<ImportFile> {
  return api("post", `${SRM_BASE}/bulk-upload`, payload);
}

export async function removeImportFile(
  id: number,
): Promise<{ success: boolean }> {
  return removeImportFileShared(id);
}

export async function getSampleSupplierFile(): Promise<{ filePath: string }> {
  return getSampleFileShared("Suppliers");
}

export async function downloadSupplierSampleFile(): Promise<void> {
  const blob = await request<Blob>("get", `${EMISSION_BASE}/samplefile`, {
    params: { type_name: "Suppliers", download: true },
    responseType: "blob",
  });
  if (!blob.size || /application\/json|text\/html/i.test(blob.type))
    throw new Error("Unable to download the supplier template");
  downloadBlob(blob, "Supplier_Import.xlsx");
}

export interface SupplierBulkUploadError {
  number: string;
  error_code: string;
  error_text: string;
  error_description: string;
  help_link: string;
}

const SUPPLIER_BULK_ERROR_DICTIONARY: Record<
  string,
  Omit<SupplierBulkUploadError, "number">
> = {
  BLK_SLR_01: {
    error_code: "BLK_SLR_01",
    error_text: "Duplicates found for provided data",
    error_description:
      "System has detected that for the provided data, there is already a duplicate record present.",
    help_link: "Link",
  },
  BLK_SLR_02: {
    error_code: "BLK_SLR_02",
    error_text: "Required columns are missing or invalid",
    error_description:
      "The uploaded sheet does not match the expected column count or required columns.",
    help_link: "Link",
  },
  BLK_SLR_03: {
    error_code: "BLK_SLR_03",
    error_text: "Column sequence mismatch",
    error_description:
      "The uploaded sheet columns are in a different order than the expected template.",
    help_link: "Link",
  },
  BLK_SLR_04: {
    error_code: "BLK_SLR_04",
    error_text: "Type field missing",
    error_description:
      "Type is a mandatory field. Without it the supplier cannot be processed.",
    help_link: "Link",
  },
  BLK_SLR_05: {
    error_code: "BLK_SLR_05",
    error_text: "Company field missing",
    error_description:
      "Company name is a mandatory field. Without it the supplier cannot be processed.",
    help_link: "Link",
  },
  BLK_SLR_06: {
    error_code: "BLK_SLR_06",
    error_text: "Primary contact name field missing",
    error_description: "Primary contact name is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_07: {
    error_code: "BLK_SLR_07",
    error_text: "Primary contact email field missing",
    error_description: "Primary contact email is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_08: {
    error_code: "BLK_SLR_08",
    error_text: "Secondary contact name field missing",
    error_description: "Secondary contact name is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_09: {
    error_code: "BLK_SLR_09",
    error_text: "Secondary contact email field missing",
    error_description: "Secondary contact email is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_10: {
    error_code: "BLK_SLR_10",
    error_text: "Industry field missing",
    error_description: "Industry type is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_11: {
    error_code: "BLK_SLR_11",
    error_text: "Registration ID field missing",
    error_description: "Registration ID is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_12: {
    error_code: "BLK_SLR_12",
    error_text: "Frequency of data request field missing",
    error_description: "Frequency of data request is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_13: {
    error_code: "BLK_SLR_13",
    error_text: "GHG Emissions field missing",
    error_description: "GHG Emissions selection is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_14: {
    error_code: "BLK_SLR_14",
    error_text: "Creation status field missing",
    error_description: "Creation Status is a mandatory field.",
    help_link: "Link",
  },
  BLK_SLR_98: {
    error_code: "BLK_SLR_98",
    error_text: "Import file not found or not accessible",
    error_description:
      "The uploaded file could not be located or accessed. Please upload the file again.",
    help_link: "Link",
  },
  BLK_SLR_99: {
    error_code: "BLK_SLR_99",
    error_text: "Unexpected error while processing file",
    error_description:
      "An unexpected error occurred while processing the uploaded file.",
    help_link: "Link",
  },
};

export function parseSupplierBulkUploadErrors(
  fileErrors: Record<string, string>,
): SupplierBulkUploadError[] {
  return Object.entries(fileErrors).map(([number, value]) => {
    const codeMatch = value.match(/(BLK_SLR_\d+)/);
    const code = codeMatch?.[1];
    const dict = code ? SUPPLIER_BULK_ERROR_DICTIONARY[code] : undefined;
    const detail = value.replace(/BLK_SLR_\d+\s*-?\s*/, "").trim();
    return {
      number,
      error_code: dict?.error_code ?? code ?? "Unknown",
      error_text: dict?.error_text ?? value,
      error_description: dict ? detail || dict.error_description : "",
      help_link: dict?.help_link ?? "",
    };
  });
}

export interface SupplierReportRequest {
  reportType: string;
  interval: string;
  fromDate: string;
  toDate: string;
  offsetMonths: number;
}

export async function generateSupplierReport(
  payload: SupplierReportRequest,
): Promise<Blob> {
  return request<Blob>("post", `${SRM_BASE}/supplier-report`, {
    data: payload,
    responseType: "blob",
    timeout: 120_000,
    unwrap: false,
  });
}

export async function getOrgSupplierFields(): Promise<string[]> {
  const raw = await api<unknown[]>("get", `${SRM_BASE}/supplier-fields`);
  return (Array.isArray(raw) ? raw : []).map((item) =>
    typeof item === "string" ? item : String(item),
  );
}

export async function createSupplierField(
  name: string,
): Promise<{ name: string }> {
  return api("post", `${SRM_BASE}/supplier-field`, { name });
}

export interface SupplierAllocationPeriod {
  month: string;
  allocated_total_emissions: number;
  name?: string;
}

export interface SupplierAllocatedEmissions {
  totalAllocatedEmissions: number;
  periodic_breakup: SupplierAllocationPeriod[];
}

export async function getSupplierAllocatedEmissions(params: {
  fromDate: string;
  toDate: string;
  fromMonth?: string;
  toMonth?: string;
}): Promise<SupplierAllocatedEmissions> {
  const user = getCurrentUser();
  return api("get", `${SRM_BASE}/supplier-allocations-total`, undefined, {
    customerID: user?.companyId || "",
    fromDate: params.fromDate,
    toDate: params.toDate,
    ...(params.fromMonth ? { fromMonth: params.fromMonth } : {}),
    ...(params.toMonth ? { toMonth: params.toMonth } : {}),
    allocationType: "",
  });
}

export async function getInvestorAllocatedEmissions(params: {
  fromDate: string;
  toDate: string;
  fromMonth?: string;
  toMonth?: string;
}): Promise<SupplierAllocatedEmissions> {
  const user = getCurrentUser();
  return api("get", `${SRM_BASE}/supplier-allocations-total`, undefined, {
    customerID: user?.companyId || "",
    fromDate: params.fromDate,
    toDate: params.toDate,
    ...(params.fromMonth ? { fromMonth: params.fromMonth } : {}),
    ...(params.toMonth ? { toMonth: params.toMonth } : {}),
    allocationType: "investments",
  });
}
